import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DownloadExcelButton, Loader, NewReactTable as TableComponent } from '../../ui/index.jsx';
import { Box, Typography, Tooltip, Radio, RadioGroup, FormControlLabel } from '@mui/material';

import { request, methods, isRequestCancelled } from '../../../../../utils/http-common.js';
import useCancellableRequest from '../../../../../hooks/useCancellableRequest.js';
import { formatNumberWithCommas } from '../../../../../helpers/numericHelpers.js';
import { buildInsightColumns, shimmerKeyframes, parseMonth, ordinalSuffix, currentDateLabel } from './shared.jsx';

const PAGE_SIZE = 50;

export default function P90View({ month, snapshotDaysMap, handleDisableTab }) {
  const { request: cancellableRequest } = useCancellableRequest('sales-pnf/forecast-report/insights/p90');

  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [columnSums, setColumnSums] = useState({});
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [snapshotDay, setSnapshotDay] = useState(0);
  const [availableDays, setAvailableDays] = useState([
    { day: 7, enabled: false },
    { day: 15, enabled: false },
    { day: 21, enabled: false },
  ]);

  const currentPageRef = useRef(1);
  const currentTotalFetchedRef = useRef(0);
  const activeSortRef = useRef([]);
  const activeFiltersRef = useRef([]);
  const dataRequestIdRef = useRef(0);
  const prevMonthRef = useRef(month);

  useEffect(() => {
    handleDisableTab(isFetching);
  }, [isFetching, handleDisableTab]);

  useEffect(() => () => {
    handleDisableTab(false);
  }, [handleDisableTab]);

  const fetchData = useCallback(async (page, sortBy, filters, selectedMonth, snapDay) => {
    if (!selectedMonth) return;
    const requestId = ++dataRequestIdRef.current;
    setIsFetching(true);
    setFetchError(null);
    let requestError;
    try {
      let res;
      [requestError, res] = await cancellableRequest('salespnf/getforecastinsights', methods.POST, {
        month: selectedMonth,
        snapshot_day: snapDay,
        page_number: page,
        page_size: PAGE_SIZE,
        sort_by: sortBy || [],
        filters: filters || [],
      }, { cancelKey: 'data' });
      if (isRequestCancelled(requestError)) return;
      if (requestError) {
        setFetchError(requestError.message || 'Failed to load insights data');
        return;
      }
      if (res?.data) {
        const rowHeader = res.data.row_header || [];
        const rowData = res.data.row_data || [];
        if (page === 1) {
          setHeaders(rowHeader);
          setRows(rowData);
          setTotalRecords(res.data.total || 0);
          currentTotalFetchedRef.current = rowData.length;
          if (res.data.additional) {
            setAvailableDays(res.data.additional);
          }
        } else {
          setRows((prev) => [...prev, ...rowData]);
          currentTotalFetchedRef.current += rowData.length;
        }
        currentPageRef.current = page;
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch insights data', e);
      }
    } finally {
      if (requestId !== dataRequestIdRef.current) return;
      if (!isRequestCancelled(requestError)) {
        setIsFetching(false);
        setLoading(false);
      }
    }
  }, [cancellableRequest]);

  const fetchTotals = useCallback(async (selectedMonth, snapDay, sortBy, filters) => {
    if (!selectedMonth) return;
    setTotalsLoading(true);
    let requestError;
    try {
      let res;
      [requestError, res] = await cancellableRequest('salespnf/getforecastinsightstotals', methods.POST, {
        month: selectedMonth,
        snapshot_day: snapDay,
        sort_by: sortBy || [],
        filters: filters || [],
      }, { cancelKey: 'totals' });
      if (isRequestCancelled(requestError)) return;
      if (requestError) {
        console.error('Failed to fetch insights totals', requestError);
        return;
      }
      if (res?.data?.row_header) {
        const sums = {};
        res.data.row_header.forEach((h) => {
          if (h.sub_label && h.sub_label !== '' && h.sub_label !== '0') {
            if (h.key === 'dealer_code') {
              sums[h.key] = `${h.sub_label} Dealers`;
            } else {
              sums[h.key] = formatNumberWithCommas(Math.round(parseFloat(h.sub_label) * 100) / 100);
            }
          }
        });
        setColumnSums(sums);
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch insights totals', e);
      }
    } finally {
      if (!isRequestCancelled(requestError)) {
        setTotalsLoading(false);
      }
    }
  }, [cancellableRequest]);

  // Sync snapshot-day options; only reset snapshot selection when month changes.
  useEffect(() => {
    if (month && snapshotDaysMap?.[month]) {
      setAvailableDays(snapshotDaysMap[month]);
    }
    if (prevMonthRef.current !== month) {
      setSnapshotDay(0);
      prevMonthRef.current = month;
    }
  }, [month, snapshotDaysMap]);

  const fetchDataRef = useRef(fetchData);
  const fetchTotalsRef = useRef(fetchTotals);
  fetchDataRef.current = fetchData;
  fetchTotalsRef.current = fetchTotals;

  // Reset and refetch on month or snapshotDay change
  useEffect(() => {
    if (!month) {
      setLoading(false);
      return;
    }
    currentPageRef.current = 1;
    currentTotalFetchedRef.current = 0;
    activeSortRef.current = [];
    activeFiltersRef.current = [];
    setRows([]);
    setColumnSums({});
    setTotalRecords(0);
    setFetchError(null);
    setLoading(true);

    fetchDataRef.current(1, [], [], month, snapshotDay);
    fetchTotalsRef.current(month, snapshotDay, [], []);
  }, [month, snapshotDay]);

  const scrollFetch = useCallback(
    (scrollEvent, sortList, filterList) => {
      if (scrollEvent) {
        const { scrollHeight, scrollTop, clientHeight } = scrollEvent;
        if (
          scrollHeight - scrollTop - clientHeight < 10 &&
          !isFetching &&
          currentTotalFetchedRef.current < totalRecords
        ) {
          fetchData(currentPageRef.current + 1, activeSortRef.current, activeFiltersRef.current, month, snapshotDay);
        }
      } else {
        activeSortRef.current = sortList || [];
        activeFiltersRef.current = filterList || [];
        currentPageRef.current = 1;
        currentTotalFetchedRef.current = 0;
        setRows([]);
        setColumnSums({});
        setTotalRecords(0);
        fetchData(1, activeSortRef.current, activeFiltersRef.current, month, snapshotDay);
        fetchTotals(month, snapshotDay, activeSortRef.current, activeFiltersRef.current);
      }
    },
    [isFetching, totalRecords, month, snapshotDay, fetchData, fetchTotals],
  );

  const handleExport = async () => {
    if (!month || isExporting) return;
    setIsExporting(true);
    try {
      const payload = {
        month,
        snapshot_day: snapshotDay,
        sort_by: activeSortRef.current,
        filters: activeFiltersRef.current,
        export: 'true',
      };
      const [, response] = await request('salespnf/getforecastinsights', methods.POST, payload, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download = disposition.split('=')[1] || `Forecast_Insights_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setIsExporting(false);
    }
  };

  const columns = buildInsightColumns(headers, columnSums, totalsLoading);
  const parsed = parseMonth(month);
  const showEmptyState = !isFetching && rows.length === 0 && headers.length > 0;

  return (
    <Box>
      <style>{shimmerKeyframes}</style>

      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', whiteSpace: 'nowrap', mr: 2 }}>
          Forecast snapshot as of
        </Typography>
        <RadioGroup
          className='radio-group'
          row
          value={String(snapshotDay)}
          onChange={(e) => setSnapshotDay(Number(e.target.value))}
          sx={{ gap: 2 }}
        >
          <FormControlLabel
            value="0"
            control={<Radio size="small" sx={{ py: 0, px: 0, '&.Mui-checked': { color: '#215093' } }} />}
            label={
              <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic' }}>
                {currentDateLabel(month)}
              </Typography>
            }
            sx={{ mr: 0.5, px: 0 }}
          />
          {availableDays.map(({ day, enabled }) => (
            <FormControlLabel
              key={day}
              value={String(day)}
              disabled={!enabled}
              control={<Radio size="small" sx={{ py: 0, px: 0, '&.Mui-checked': { color: '#215093' } }} />}
              label={
                <Typography variant="body2" sx={{ color: enabled ? '#555' : '#bbb', fontStyle: 'italic' }}>
                  {day}{ordinalSuffix(day)} {parsed?.fullMonth || ''}
                </Typography>
              }
              sx={{ mr: 0.5, px: 0 }}
            />
          ))}
        </RadioGroup>
        <Box flex={1} />
        {/* {totalRecords > 0 && (
          <Typography variant="caption" sx={{ color: '#888', whiteSpace: 'nowrap' }}>
            {totalRecords.toLocaleString()} dealers
          </Typography>
        )} */}
        <DownloadExcelButton onClick={handleExport} loading={isExporting} />
      </Box>

      {fetchError && (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography color="error">{fetchError}</Typography>
        </Box>
      )}

      {!fetchError && loading ? (
        <Loader loading={loading} />
          ) : !fetchError && showEmptyState ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body1" sx={{ color: "#888" }}>
                No P90 dealers with zero purchases.
              </Typography>
            </Box>
          ) : !fetchError ? (
            <div
              className="jklc-table-wrapper forecast-report-table"
              style={{ marginBottom: 0 }}
            >
              <style>{`
                .jklc-table-wrapper .table-container {
                  max-height: calc(100vh - 370px) !important;
                }
              `}</style>

              <TableComponent
                columns={columns}
                data={rows}
                hidePagination
                isFetching={isFetching}
                totalRecords={totalRecords}
                scrollFetch={scrollFetch}
                stickyColumnCount={2}
              />
            </div>
          ) : null}

     
    </Box>
  );
}
